/* ==========================================================================
   Radiator Cap Pressure Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-rcc]');
  if (!root) return;

  /* Boiling point of ethylene glycol mixtures at sea level, unpressurised. */
  var BOIL_BY_PCT = [
    { pct: 0, f: 212 }, { pct: 20, f: 216 }, { pct: 30, f: 220 },
    { pct: 40, f: 222 }, { pct: 50, f: 226 }, { pct: 60, f: 230 }, { pct: 70, f: 235 }
  ];

  /* Each psi of system pressure raises the boiling point by roughly this much. */
  var F_PER_PSI = 3.0;

  /* Boiling point drops with altitude as ambient pressure falls. */
  var F_PER_1000FT = 1.9;

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function boilAt(pct) {
    if (pct <= BOIL_BY_PCT[0].pct) return BOIL_BY_PCT[0].f;
    for (var i = 1; i < BOIL_BY_PCT.length; i++) {
      if (pct <= BOIL_BY_PCT[i].pct) {
        var a = BOIL_BY_PCT[i - 1], b = BOIL_BY_PCT[i];
        return a.f + (b.f - a.f) * ((pct - a.pct) / (b.pct - a.pct));
      }
    }
    return BOIL_BY_PCT[BOIL_BY_PCT.length - 1].f;
  }

  function compute(input) {
    if (!isFinite(input.cap) || input.cap < 0) {
      return { ok: false, errors: ['Enter the cap pressure rating.'] };
    }
    var mix = isFinite(input.mix) ? Math.max(0, Math.min(70, input.mix)) : 50;
    var alt = isFinite(input.altitude) && input.altitude > 0 ? input.altitude : 0;

    var base = boilAt(mix);
    var altLoss = (alt / 1000) * F_PER_1000FT;
    var unpressurised = base - altLoss;
    var boil = unpressurised + input.cap * F_PER_PSI;

    var figures = [
      { label: 'Boiling point', value: num(boil, 0) + ' F',
        sub: 'The ' + num(input.cap, 1) + ' psi cap is worth ' + num(input.cap * F_PER_PSI, 0) + ' degrees on its own.' },
      { label: 'Without the cap', value: num(unpressurised, 0) + ' F',
        sub: 'What a ' + num(mix, 0) + '% mix boils at unpressurised' + (alt > 0 ? ' at ' + comma(alt) + ' ft.' : '.') }
    ];

    var status, warnings = [];
    if (boil >= 260) {
      status = { key: 'good', label: num(boil, 0) + 'F',
                 note: 'Good margin over normal operating temperature.' };
    } else if (boil >= 240) {
      status = { key: 'monitor', label: num(boil, 0) + 'F',
                 note: 'Adequate, but not much margin on a hot day sitting in traffic.' };
    } else {
      status = { key: 'soon', label: num(boil, 0) + 'F',
                 note: 'Thin margin. Most engines run a thermostat around 195F and coolant temperature climbs well past that under load.' };
    }

    var rows = [
      { head: 'System' },
      { label: 'Cap rating', value: num(input.cap, 1) + ' psi' },
      { label: 'Coolant mix', value: num(mix, 0) + '%' },
      { label: 'Base boiling point', value: num(base, 0) + ' F' }
    ];
    if (alt > 0) {
      rows.push({ label: 'Altitude', value: comma(alt) + ' ft' });
      rows.push({ label: 'Lost to altitude', value: '-' + num(altLoss, 0) + ' F' });
    }
    rows.push({ label: 'Gained from cap', value: '+' + num(input.cap * F_PER_PSI, 0) + ' F' });
    rows.push({ label: 'Boiling point', value: num(boil, 0) + ' F' });

    if (isFinite(input.actual) && input.actual > 0 && input.actual < input.cap) {
      var weakBoil = unpressurised + input.actual * F_PER_PSI;
      rows.push({ head: 'Your tested cap' });
      rows.push({ label: 'Released at', value: num(input.actual, 1) + ' psi' });
      rows.push({ label: 'Boiling point', value: num(weakBoil, 0) + ' F' });
      rows.push({ label: 'Lost', value: num(boil - weakBoil, 0) + ' F' });
      figures.push({ label: 'Your weak cap costs', value: num(boil - weakBoil, 0) + ' F',
        sub: 'Releasing at ' + num(input.actual, 1) + ' psi instead of ' + num(input.cap, 1) + '.' });
      warnings.push('A cap that releases early is one of the most common causes of overheating that survives a new radiator, new thermostat and fresh coolant. Caps are cheap and are worth replacing on suspicion.');
      status = { key: 'now', label: num(weakBoil, 0) + 'F',
                 note: 'A weak cap has taken ' + num(boil - weakBoil, 0) + ' degrees of boiling margin away.' };
    }

    rows.push({ head: 'What each cap is worth' });
    [10, 13, 15, 16, 18, 21].forEach(function (p) {
      rows.push({ label: p + ' psi' + (Math.abs(p - input.cap) < 0.5 ? '  (yours)' : ''),
                  value: num(unpressurised + p * F_PER_PSI, 0) + ' F' });
    });

    return { ok: true, errors: [], warnings: warnings, status: status, figures: figures, rows: rows };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "cap",
          "key": "c",
          "type": "number"
      },
      {
          "name": "mix",
          "key": "m",
          "type": "number"
      },
      {
          "name": "altitude",
          "key": "a",
          "type": "number"
      },
      {
          "name": "actual",
          "key": "ac",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-rcc__figure">'
        + '<div class="pc-rcc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-rcc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-rcc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-rcc__row pc-rcc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-rcc__row">'
        + '<span class="pc-rcc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-rcc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_RCC = { compute: compute };
})();
